import React, { useState, useEffect } from "react";
import { trackEvent } from "../utils/analytics";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
      
      // Track navigation click
      trackEvent('klikniecie_nawigacji', {
        category: 'Nawigacja',
        label: sectionId,
        section: sectionId
      });
    }
  };

  const handleLogoClick = () => {
    trackEvent('klikniecie_logo', {
      category: 'Nawigacja',
      label: 'Logo',
      section: 'naglowek'
    });
    scrollToSection('hero');
  };

  const handlePhoneClick = () => {
    trackEvent('telefon_naglowek', {
      category: 'Nawigacja',
      label: 'Numer telefonu w nagłówku',
      phone_number: '+48602476604'
    });
  };

  const handleMobileMenuToggle = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    
    trackEvent('menu_mobilne_przelacz', {
      category: 'Nawigacja',
      label: newState ? 'Otwórz' : 'Zamknij',
      action: newState ? 'otworz' : 'zamknij'
    });
  };

  const handleMobileContactClick = (type, value) => {
    const eventName = type === 'email' ? 'email_menu_mobilne' : 'telefon_menu_mobilne';
    const labelText = type === 'email' ? 'Email w menu mobilnym' : 'Telefon w menu mobilnym';
    
    trackEvent(eventName, {
      category: 'Nawigacja',
      label: labelText,
      [type]: value
    });
  };

  const navItems = [
    { label: "Oferta", id: "offer" },
    { label: "O nas", id: "about" },
    { label: "Dlaczego my?", id: "choose-us" },
    { label: "Kontakt", id: "contact" },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-black/80 backdrop-blur-sm shadow-lg"
            : "bg-black/50 backdrop-blur-sm"
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-12 md:h-16">
            {/* Logo and Phone - Left Side */}
            <div className="flex items-center space-x-6">
              <button
                onClick={handleLogoClick}
                className="transition-opacity hover:opacity-80"
              >
                <img src="/pictures/logo.svg" alt="LUMAT" className="pb-1" />
              </button>
              <div className="hidden md:block w-px h-4 bg-[rgba(159,161,167,1)]" />
              <div className="hidden md:flex items-center space-x-1 text-sm">
                <span className="text-white/90">
                  <a href="tel:+48602476604" onClick={handlePhoneClick}>tel. 602 476 604</a>
                </span>
              </div>
            </div>

            {/* Desktop Navigation - Right Side */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="transition-colors hover:text-primary-500 text-white"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={handleMobileMenuToggle}
                className="p-2 rounded-md text-white"
                aria-label="Otwórz menu mobilne"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMobileMenuOpen ? (
                    <path d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full Screen - Completely Outside nav */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-[#0F1523]">
          <div className="flex flex-col h-full w-full">
            {/* Close Button */}
            <div className="flex justify-end p-6">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-white hover:text-primary-500 transition-colors"
                aria-label="Zamknij menu"
              >
                <svg
                  className="w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Logo */}
            <div className="flex justify-center">
              <img src="/pictures/logo.svg" alt="LUMAT" className="h-12" />
            </div>
            {/* Navigation Items - Centered */}
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Navigation Items */}
              <div className="w-full space-y-8">
                {navItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-center text-white font-heading text-5xl font-light hover:text-primary-500 transition-all duration-300 py-4 transform ${
                      isMobileMenuOpen
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                    style={{
                      transitionDelay: `${index * 150}ms`,
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Bottom Section - Contact */}
            <div className="px-6 pb-24">
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center gap-10">
                  <a
                    href="mailto:biuro@lumat.com.pl"
                    className="text-blue-400 hover:text-blue-300 transition-colors text-lg"
                    onClick={() => handleMobileContactClick('email', 'biuro@lumat.com.pl')}
                  >
                    biuro@lumat.com.pl
                  </a>
                  <a
                    href="tel:+48602476604"
                    className="text-blue-400 hover:text-blue-300 transition-colors text-lg"
                    onClick={() => handleMobileContactClick('phone', '+48602476604')}
                  >
                    602 476 604
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
